export const TRAINING_TEMPLATE_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.txt'
export const TRAINING_TEMPLATE_MAX_FILE_MB = 100
export const TRAINING_TEMPLATE_FILE_HELPER = 'pdf, doc, docx, ppt, pptx or txt (Max: 100MB)'

export const TRAINING_TEMPLATE_VIDEO_ACCEPT = '.mp4,.mov,.avi,.mpeg,.webm'
export const TRAINING_TEMPLATE_VIDEO_MAX_FILE_MB = 200
export const TRAINING_TEMPLATE_VIDEO_HELPER = 'mp4, mov, avi, mpeg or webm (Max: 200MB)'

/** Single picker that accepts either a source document or a training video. */
export const TRAINING_TEMPLATE_SOURCE_ACCEPT = `${TRAINING_TEMPLATE_ACCEPT},${TRAINING_TEMPLATE_VIDEO_ACCEPT}`
export const TRAINING_TEMPLATE_SOURCE_MAX_FILE_MB = TRAINING_TEMPLATE_VIDEO_MAX_FILE_MB
export const TRAINING_TEMPLATE_SOURCE_HELPER =
  'pdf, doc, docx, ppt, pptx, txt, mp4, mov, avi, mpeg or webm (Max: 200MB)'

export function isTrainingTemplateVideoFile(file) {
  if (!(file instanceof File)) return false
  if (typeof file.type === 'string' && file.type.startsWith('video/')) return true
  const name = String(file.name || '').toLowerCase()
  return TRAINING_TEMPLATE_VIDEO_ACCEPT.split(',')
    .map((ext) => ext.trim().toLowerCase())
    .filter(Boolean)
    .some((ext) => name.endsWith(ext))
}

export const TRAINING_TEMPLATE_STATUS_LABELS = {
  pending: 'Pending',
  in_process: 'In Process',
  completed: 'Completed',
  failed: 'Failed',
}

export const TRAINING_TEMPLATE_STATUS_OPTIONS = Object.entries(TRAINING_TEMPLATE_STATUS_LABELS).map(
  ([value, label]) => ({ label, value }),
)

export const TRAINING_TEMPLATE_PROCESSING_STATUSES = ['pending', 'in_process']

export function trainingTemplateStatusLabel(status) {
  return TRAINING_TEMPLATE_STATUS_LABELS[status] ?? String(status ?? 'Unknown').replaceAll('_', ' ')
}

export function isTrainingTemplateProcessing(status) {
  return TRAINING_TEMPLATE_PROCESSING_STATUSES.includes(status)
}

export function hasExtractedTrainingData(data) {
  if (!data || typeof data !== 'object') return false
  return Boolean(
    data.title
    || data.description
    || data.owner_name
    || data.owner_email
    || (Array.isArray(data.quiz_questions) && data.quiz_questions.length),
  )
}

export function unwrapTrainingTemplatesPayload(data) {
  return {
    templates: data?.templates ?? (Array.isArray(data) ? data : []),
    pagination: data?.pagination ?? {},
  }
}

export function unwrapTrainingTemplate(data) {
  if (!data || typeof data !== 'object') return data
  const nested = data.training_template ?? data.template
  if (!nested || typeof nested !== 'object') return data

  // Some responses nest the model and leave media fields on the envelope.
  return {
    ...nested,
    video_url: nested.video_url ?? data.video_url ?? nested.video?.video_url ?? nested.video?.url,
    video_file_name: nested.video_file_name ?? data.video_file_name ?? nested.video?.file_name ?? nested.video?.name,
    presentation_url: nested.presentation_url ?? data.presentation_url,
    presentation_filename: nested.presentation_filename ?? data.presentation_filename,
    has_video: nested.has_video ?? data.has_video,
  }
}

/** Resolve an uploaded template video URL from common API shapes. */
export function templateVideoUrl(template) {
  if (!template || typeof template !== 'object') return ''
  return String(
    template.video_url
    ?? template.video?.video_url
    ?? template.video?.url
    ?? template.video?.file_url
    ?? '',
  ).trim()
}

export function templateVideoFileName(template) {
  if (!template || typeof template !== 'object') return ''
  return String(
    template.video_file_name
    ?? template.video?.file_name
    ?? template.video?.name
    ?? template.video?.original_filename
    ?? '',
  ).trim()
}

export function firstUploadedFile(value) {
  if (value instanceof File) return value
  if (Array.isArray(value)) return value.find((item) => item instanceof File) ?? null
  return null
}

export function trainingTemplateHasUpload(payload) {
  if (payload instanceof FormData) return payload.has('file') || payload.has('video')
  return Boolean(firstUploadedFile(payload?.file) || firstUploadedFile(payload?.video))
}

function isBlank(value) {
  return value == null || String(value).trim() === ''
}

/** True when a selected template already has a generated presentation to reuse. */
export function hasTemplatePresentation(source) {
  return !isBlank(source?.presentation_url) || !isBlank(source?.presentation_filename)
}

/** True when a selected template already has an uploaded training video to reuse. */
export function hasTemplateVideo(source) {
  return !isBlank(templateVideoUrl(source))
}

/** Prefer a completed chat video, then a template-uploaded video URL on the form. */
export function existingTrainingVideoUrl(form) {
  if (!form || typeof form !== 'object') return ''
  const video = form.training_video && typeof form.training_video === 'object' ? form.training_video : {}
  return String(
    video.video_url
    ?? video.url
    ?? video.file_url
    ?? form.video_url
    ?? templateVideoUrl(form)
    ?? '',
  ).trim()
}

/** Generate slides only for custom sources, or templates missing a presentation. */
export function needsPresentationGeneration(form) {
  if (form?.pathway === 'template' && form.template_id) {
    return isBlank(form.presentation_url) && isBlank(form.presentation_filename)
  }
  return true
}

export function unwrapGeneratedPresentation(form) {
  const generated = form?.generated_presentation
  return generated && typeof generated === 'object' ? (generated.presentation ?? generated) : {}
}

export function isWorkspaceFileUrl(fileUrl) {
  if (!fileUrl) return false
  try {
    const parsed = new URL(resolveTrainingTemplateFileUrl(fileUrl), window.location.href)
    return WORKSPACE_FILE_PATH.test(parsed.pathname)
  } catch {
    return false
  }
}

/** True when the preview must be fetched with the session cookie (workspace / storage / API origin). */
export function isAuthenticatedFileUrl(fileUrl) {
  if (!fileUrl) return false
  if (isWorkspaceFileUrl(fileUrl)) return true

  let parsed
  try {
    parsed = new URL(resolveTrainingTemplateFileUrl(fileUrl), window.location.href)
  } catch {
    return false
  }

  if (parsed.pathname.startsWith('/storage/')) return true
  if (typeof window !== 'undefined' && parsed.origin === window.location.origin) return true

  try {
    return parsed.origin === new URL(import.meta.env.VITE_BASE_API_URL).origin
  } catch {
    return false
  }
}

export function generatedOutputFile(form) {
  if (form?.output_format === 'video') {
    const video = form.training_video && typeof form.training_video === 'object' ? form.training_video : {}
    return {
      url: existingTrainingVideoUrl(form),
      name: video.title ?? form.video_file_name ?? form.title ?? '',
      mime: 'video/mp4',
    }
  }

  const payload = unwrapGeneratedPresentation(form)
  return {
    url:
      payload.file_url
      ?? payload.url
      ?? payload.download_url
      ?? payload.presentation_url
      ?? form?.presentation_url
      ?? '',
    name:
      payload.original_filename
      ?? payload.filename
      ?? payload.file_name
      ?? payload.name
      ?? payload.title
      ?? payload.presentation_filename
      ?? form?.presentation_filename
      ?? '',
    mime: payload.mime_type ?? payload.content_type ?? payload.type ?? '',
  }
}

/** HTML slide decks hosted at /workspaces/{id}/training/{id} (not compliance-training). */
export function isWebPresentationUrl(url = '', name = '', mime = '') {
  const hay = `${url} ${name} ${mime}`.toLowerCase()
  if (hay.includes('text/html') || /\.html?(\?|$)/i.test(hay)) return true
  const path = String(url).split('?')[0].split('#')[0]
  return /\/workspaces\/\d+\/training\/\d+\/?$/i.test(path)
}

/** Extension match that works on a multi-field haystack (not only string end). */
const EXT = (exts) => new RegExp(`\\.(?:${exts})(?=$|[?#\\s])`, 'i')

export function generatedOutputKind({ url = '', name = '', mime = '', outputFormat = '' } = {}) {
  const hay = `${url} ${name} ${mime} ${outputFormat}`.toLowerCase()
  if (outputFormat === 'video' || EXT('mp4|webm|mov|m4v').test(hay) || hay.includes('video/')) {
    return 'video'
  }
  // Office/archives must never be treated as inline PDF (iframes trigger auto-download).
  if (EXT('ppt|pptx|doc|docx|xls|xlsx|zip|csv').test(hay)
    || hay.includes('officedocument')
    || hay.includes('msword')
    || hay.includes('ms-excel')
    || hay.includes('ms-powerpoint')) {
    return 'file'
  }
  // Inline HTML presentations are safe to iframe (unlike Office binaries).
  if (isWebPresentationUrl(url, name, mime)) return 'web'
  if (EXT('pdf').test(hay) || hay.includes('application/pdf')) return 'pdf'
  // Generated "slides" without a concrete URL yet — prefer PDF when produced as a file.
  if (outputFormat === 'slides') return isWebPresentationUrl(url, name, mime) ? 'web' : 'pdf'
  return 'file'
}

export function resolveTrainingTemplateFileUrl(fileUrl) {
  if (!fileUrl) return ''
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl
  const path = `/${String(fileUrl).replace(/^\/+/, '')}`
  // HTML decks live on the API host (`/workspaces/{id}/training/{id}`), not the Vite SPA.
  if (isWebPresentationUrl(path)) {
    try {
      const apiOrigin = new URL(import.meta.env.VITE_BASE_API_URL).origin
      return `${apiOrigin}${path}`
    } catch {
      // fall through
    }
  }
  return `${window.location.origin}${path}`
}

/**
 * URL for native media elements (<video>, <audio>).
 * Prefer same-origin `/storage/...` so the Vite/API proxy can attach cookies and
 * the browser can stream (Range requests) instead of downloading a blob first.
 */
export function resolvePlayableMediaUrl(fileUrl) {
  if (!fileUrl) return ''
  const resolved = resolveTrainingTemplateFileUrl(fileUrl)
  try {
    const parsed = new URL(resolved, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
    if (parsed.pathname.startsWith('/storage/')) {
      if (import.meta.env.DEV || parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}`
      }
    }
  } catch {
    // fall through
  }
  return resolved
}

const WORKSPACE_FILE_PATH = /^(?:\/api)?\/workspaces\/(\d+)\/files\/(\d+)(?:\/download)?\/?$/

/**
 * Request for fetching a file as a blob without tripping CORS.
 * Workspace file links (`/workspaces/{id}/files/{id}`) live outside `/api`
 * and have no CORS headers — use the authenticated download endpoint instead.
 * `/storage/` is same-origin only when the SPA host proxies it (dev, or
 * VITE_API_SAME_ORIGIN). Otherwise the absolute storage URL has no CORS
 * headers and must not be requested through the API client.
 */
export function resolveUploadFileRequest(fileUrl) {
  const resolved = resolveTrainingTemplateFileUrl(fileUrl)
  if (!resolved) return null

  let parsed
  try {
    parsed = new URL(resolved, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
  } catch {
    return { url: resolved }
  }

  const fileMatch = parsed.pathname.match(WORKSPACE_FILE_PATH)
  if (fileMatch) {
    return { url: `/workspaces/${fileMatch[1]}/files/${fileMatch[2]}/download` }
  }

  const sameOriginStorage =
    import.meta.env.DEV || import.meta.env.VITE_API_SAME_ORIGIN === 'true'
  if (parsed.pathname.startsWith('/storage/') && sameOriginStorage) {
    return { url: `${parsed.pathname}${parsed.search}`, baseURL: '' }
  }

  if (!import.meta.env.DEV) return { url: resolved }

  try {
    const apiOrigin = new URL(import.meta.env.VITE_BASE_API_URL).origin
    if (parsed.origin === apiOrigin) {
      const path = parsed.pathname.startsWith('/api/') ? parsed.pathname : `/api${parsed.pathname}`
      return { url: `${path}${parsed.search}`, baseURL: '' }
    }
  } catch {
    // fall through to the original URL
  }

  return { url: resolved }
}

/**
 * False for `/storage/` URLs that are not served through the SPA proxy.
 * The API host's storage is public, but it does not send CORS headers, and
 * the SPA host does not serve those files itself.
 */
export function canFetchUploadFile(fileUrl) {
  const request = resolveUploadFileRequest(fileUrl)
  if (!request?.url) return false

  let parsed
  try {
    parsed = new URL(
      request.url,
      typeof window !== 'undefined' ? window.location.href : 'http://localhost',
    )
  } catch {
    return false
  }

  if (!parsed.pathname.startsWith('/storage/')) return true
  return request.baseURL === ''
}

export function buildTrainingTemplateFormData(payload = {}) {
  const formData = new FormData()
  const file = firstUploadedFile(payload.file)

  if (file) formData.append('file', file)
  if (payload.country_id != null && payload.country_id !== '') {
    formData.append('country_id', String(payload.country_id))
  }
  if (payload.name != null && payload.name !== '') formData.append('name', String(payload.name))
  if (payload.description != null) formData.append('description', String(payload.description))
  if (payload.is_active != null) formData.append('is_active', payload.is_active ? '1' : '0')
  if (payload.training_data != null) {
    formData.append(
      'training_data',
      typeof payload.training_data === 'string'
        ? payload.training_data
        : JSON.stringify(payload.training_data),
    )
  }

  const video = firstUploadedFile(payload.video)
  if (video) formData.append('video', video)

  if (Array.isArray(payload.workspace_ids) && payload.workspace_ids.length) {
    formData.append('workspace_ids', JSON.stringify(payload.workspace_ids.map((id) => Number(id))))
  }

  return formData
}

function normalizeQuizOption(option) {
  if (option == null) return ''
  if (typeof option === 'string' || typeof option === 'number') return String(option)
  if (typeof option === 'object') {
    return String(option.text ?? option.label ?? option.option ?? option.value ?? '')
  }
  return String(option)
}

function normalizeQuizOptions(question = {}) {
  const raw = question.options ?? question.answers ?? question.choices
  if (Array.isArray(raw)) return raw.map(normalizeQuizOption)
  if (raw && typeof raw === 'object') return Object.values(raw).map(normalizeQuizOption)
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(/\n|\|/).map((part) => part.trim()).filter(Boolean)
  }
  return ['', '', '']
}

export function mapQuizQuestionsFromTemplate(questions = []) {
  if (!Array.isArray(questions) || !questions.length) {
    return [normalizeQuizQuestion()]
  }

  return questions.map((question) => normalizeQuizQuestion(question))
}

export function normalizeQuizQuestion(question = {}) {
  const options = normalizeQuizOptions(question)
  while (options.length < 3) options.push('')
  return {
    ...question,
    question: String(question.question ?? '').trim() || String(question.title ?? '').trim(),
    options,
    correct_answer: mapQuizCorrectAnswer(question, options),
  }
}

export function mapQuizCorrectAnswer(question = {}, options = []) {
  const raw = question.correct_answer ?? question.correct_option ?? question.answer ?? question.correct
  if (raw === '' || raw == null) return ''

  const opts = options.length ? options : normalizeQuizOptions(question)
  const asNumber = Number(raw)
  if (Number.isInteger(asNumber) && asNumber >= 0 && asNumber <= 4) {
    if (String(opts[asNumber] ?? '').trim()) return String(asNumber)
    if (asNumber >= 1 && String(opts[asNumber - 1] ?? '').trim()) return String(asNumber - 1)
    return String(asNumber)
  }

  const letter = String(raw).trim().toUpperCase()
  const letterMatch = letter.match(/^(?:OPTION\s*)?([A-E])\)?$/)
  if (letterMatch) return String(['A', 'B', 'C', 'D', 'E'].indexOf(letterMatch[1]))

  const text = String(raw).trim().toLowerCase()
  const idx = opts.findIndex((option) => String(option ?? '').trim().toLowerCase() === text)
  return idx >= 0 ? String(idx) : ''
}

export function isQuizQuestionComplete(question = {}) {
  const text = String(question.question ?? '').trim()
  const options = Array.isArray(question.options) ? question.options : []
  const filledOptions = options.filter((option) => String(option ?? '').trim()).length
  const answerIndex = Number(question.correct_answer)
  const hasAnswer =
    question.correct_answer !== '' &&
    question.correct_answer != null &&
    Number.isInteger(answerIndex) &&
    String(options[answerIndex] ?? '').trim() !== ''
  return Boolean(text && filledOptions >= 3 && hasAnswer)
}

export function unwrapTrainingAnswers(data) {
  if (!data || typeof data !== 'object') return {}
  const nested = data.training ?? data.training_data ?? data.answers ?? data
  const payload = nested && typeof nested === 'object' ? { ...nested } : {}
  if (!Array.isArray(payload.quiz_questions)) {
    payload.quiz_questions = data.quiz_questions ?? data.questions ?? nested?.questions
  }
  if (payload.title == null) payload.title = data.title ?? data.name
  return payload
}

export function applyTrainingDataToForm(form, data, departments = []) {
  if (!form || !data) return form

  if (data.title) form.title = data.title
  if (data.name && !form.title) form.title = data.name
  if (data.description != null) form.description = data.description
  if (data.effective_date) form.effective_date = data.effective_date
  if (data.owner_name) form.owner_name = data.owner_name
  else if (typeof data.owner === 'string' && data.owner) form.owner_name = data.owner
  if (data.owner_email) form.owner_email = data.owner_email
  if (data.passing_score != null && data.passing_score !== '') {
    const passingScore = Number(String(data.passing_score).replace(/%/g, '').trim())
    if (Number.isFinite(passingScore)) form.passing_score = passingScore
  }
  if (data.max_attempts != null && data.max_attempts !== '') {
    const maxAttempts = Number(String(data.max_attempts).trim())
    if (Number.isFinite(maxAttempts)) form.max_attempts = maxAttempts
  }
  if (typeof data.has_quiz === 'boolean') form.has_quiz = data.has_quiz
  else if (data.has_quiz === 1 || data.has_quiz === '1') form.has_quiz = true
  else if (data.has_quiz === 0 || data.has_quiz === '0') form.has_quiz = false
  if (Array.isArray(data.quiz_questions) && data.quiz_questions.length) {
    form.quiz_questions = mapQuizQuestionsFromTemplate(data.quiz_questions)
    form.quiz_mode = 'ai'
    form.has_quiz = true
  }

  if (data.department_id) {
    form.department_id = data.department_id
  } else if (data.department?.id) {
    form.department_id = data.department.id
  } else if (data.department_name || data.department?.name) {
    const departmentName = data.department_name || data.department?.name
    const match = departments.find(
      (department) =>
        department.label?.trim().toLowerCase() === String(departmentName).trim().toLowerCase(),
    )
    if (match) form.department_id = match.value
  }

  return form
}
